/**
 * Reliable print module using hidden iframe.
 * Avoids popup blockers that block window.open().
 */

let printFrameId = 0;

/**
 * Print HTML content via a hidden iframe.
 * This approach works in all browsers without popup blocker issues.
 *
 * @param {string} html - The full HTML document to print
 * @param {object} options - Optional settings
 * @param {string} options.title - Document title for the print dialog
 * @param {boolean} options.autoClose - Auto-close iframe after printing (default: true)
 * @param {number} options.width - iframe width (default: 800)
 * @param {number} options.height - iframe height (default: 600)
 * @returns {boolean} true if print was initiated
 */
export const printHtml = (html, options = {}) => {
  if (typeof window === "undefined" || !html) return false;

  const {
    title = "Impression",
    autoClose = true,
    width = 800,
    height = 600,
  } = options;

  printFrameId += 1;
  const frameName = `almoune_print_frame_${printFrameId}`;

  // Remove any existing print iframe
  const existing = window.document.getElementById(frameName);
  if (existing) existing.remove();

  // Create a hidden iframe
  const iframe = window.document.createElement("iframe");
  iframe.id = frameName;
  iframe.name = frameName;
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = `${width}px`;
  iframe.style.height = `${height}px`;
  iframe.style.border = "none";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  iframe.style.zIndex = "-1";

  window.document.body.appendChild(iframe);

  const iframeDoc = iframe.contentWindow || iframe.contentDocument;
  if (!iframeDoc || !iframeDoc.document) {
    return printHtmlFallback(html, title);
  }

  iframeDoc.document.open();
  iframeDoc.document.write(html);
  iframeDoc.document.close();

  // Wait for content to load, then print
  const doPrint = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      if (autoClose) {
        setTimeout(() => {
          if (iframe.parentNode) iframe.remove();
        }, 2000);
      }
      return true;
    } catch (err) {
      console.warn("Print failed, falling back to window.open:", err);
      return printHtmlFallback(html, title);
    }
  };

  // Small delay to ensure content is rendered
  setTimeout(doPrint, 300);
  return true;
};

/**
 * Fallback print using window.open (for environments where iframe fails).
 */
const printHtmlFallback = (html, title) => {
  try {
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=800,height=600");
    if (!printWindow) return false;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    return true;
  } catch {
    return false;
  }
};
